// src/hooks/useNavigationBlocker.js
//
// useNavigationBlocker
// --------------------
// Central hook for “unsaved changes / in-progress voucher” protection.
//
// Behaviours:
//   1) Hard unload (tab close / refresh)
//      - When `isBlocking` and opts.warnOnHardUnload=true:
//          → show native browser prompt via `beforeunload`.
//      - IMPORTANT: this handler **MUST NOT** call any async API.
//        At this moment the page is already being closed.
//
//   2) Back/forward navigation (browser history)
//      - When `isBlocking`:
//          → intercept `popstate`, ask user to confirm.
//          → if user cancels, push history state back to stay on page.
//          → if user confirms, optionally call `onSoftLeave()`.
//
//   3) Soft navigation via clicks inside the SPA
//      - When `isBlocking` and user clicks:
//          .sidebar-link
//          .sidebar-dropdown-btn
//          .change-password-button
//          .logout-button
//          a[href]
//        → stop the original navigation,
//        → show confirm,
//        → if cancelled: do nothing,
//        → if confirmed:
//            * call `onSoftLeave()` (for LOCAL cleanup / analytics;
//              server-side voucher cancel should be done elsewhere),
//            * **temporarily remove our click handler**, then call
//              `target.click()` again so React Router / original handlers
//              can proceed **without** a full page reload.
//
// Parameters:
//   - isBlocking   : boolean – when true, navigation is guarded.
//   - onSoftLeave  : optional function, called ONLY on soft navigation
//                    when the user confirms leaving. No server calls in here.
//   - opts         : { warnOnHardUnload: boolean } (default: true)
//                    Set to false if you don't want the browser-level prompt
//                    on reload/close.
//
import { useEffect } from "react";

export default function useNavigationBlocker(
  isBlocking,
  onSoftLeave,
  opts = { warnOnHardUnload: true }
) {
  // ---------------------------------------------------------------------------
  // 1) Hard unload guard (tab close / refresh)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isBlocking || !opts?.warnOnHardUnload) return;

    const onBeforeUnload = (e) => {
      // Setting returnValue triggers the browser's native dialog:
      //   "Do you want to leave this site?"
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isBlocking, opts?.warnOnHardUnload]);

  // ---------------------------------------------------------------------------
  // 2) Back/forward navigation (browser history)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isBlocking) return;

    const onPopState = () => {
      const ok = window.confirm(
        "You have an in-progress voucher. Leave this page?"
      );
      if (ok) {
        if (typeof onSoftLeave === "function") {
          try {
            onSoftLeave();
          } catch {
            /* no-op */
          }
        }
        // Allow back/forward to proceed.
        return;
      }
      // Cancel navigation by pushing current URL back onto history.
      window.history.pushState(null, "", window.location.href);
    };

    // Seed history so the first "back" stays inside the app.
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [isBlocking, onSoftLeave]);

  // ---------------------------------------------------------------------------
  // 3) Intercept sidebar / navbar / generic links (soft navigation)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isBlocking) return;

    // Elements that, when clicked, should be guarded.
    const selector =
      ".sidebar-link, .sidebar-dropdown-btn, .change-password-button, .logout-button, a[href]";

    // NOTE:
    //  - This handler is attached directly to DOM nodes, not React handlers.
    //  - We **do not** use window.location.assign() anymore; instead we
    //    replay the original click after confirmation to keep SPA behaviour.
    const handler = (event) => {
      if (!isBlocking) return;

      // Only react to primary button (left click)
      if (event.button !== 0) return;

      // Stop the original navigation / handler first.
      event.preventDefault();
      event.stopImmediatePropagation();

      const ok = window.confirm(
        "You have an in-progress voucher. Leave this page?"
      );
      if (!ok) return;

      // Local side-effect (analytics / UI cleanup).
      // DO NOT call any server-side cancel here.
      if (typeof onSoftLeave === "function") {
        try {
          onSoftLeave();
        } catch {
          /* no-op */
        }
      }

      const target = event.currentTarget;

      // IMPORTANT TRICK:
      //  - Remove our handler from this element so the next click
      //    is handled only by React Router / the original onClick.
      //  - Then programmatically trigger the click again.
      //  - We do NOT re-attach here, because in most cases navigation
      //    will unmount this page and the hook will be cleaned up anyway.
      target.removeEventListener("click", handler);
      target.click();
    };

    const elements = Array.from(document.querySelectorAll(selector));
    elements.forEach((el) => el.addEventListener("click", handler));

    return () => {
      elements.forEach((el) => el.removeEventListener("click", handler));
    };
  }, [isBlocking, onSoftLeave]);

  // Currently we only expose the flag (if some component wants to read it)
  return { isBlocking: !!isBlocking };
}
